import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Printer, Download } from "lucide-react";

const sections = [
  {
    id: "personal",
    title: "Persönliche Daten",
    fields: [
      "Vor- und Nachname",
      "Geburtsdatum",
      "E-Mail-Adresse",
      "Telefonnummer",
      "Wohnort",
    ],
  },
  {
    id: "anliegen",
    title: "Angaben zu deinem Anliegen",
    fields: [
      "Welche Leistung hast du gebucht?",
      "Was ist dein aktuelles Anliegen?",
      "Welche Themen beschäftigen dich derzeit besonders?",
      "Gibt es konkrete Fragen, die du beantwortet haben möchtest?",
      "Seit wann besteht das Thema?",
      "Was wünschst du dir als Ergebnis unserer gemeinsamen Arbeit?",
    ],
  },
  {
    id: "energie",
    title: "Energetische Situation",
    fields: [
      "Wie fühlst du dich aktuell auf einer Skala von 1–10? (Körperlich / Emotional / Mental / Spirituell)",
      "Hast du bereits Erfahrungen mit energetischer Arbeit, Readings oder spiritueller Begleitung?",
      "Falls ja, welche?",
      "Gibt es derzeit besondere Herausforderungen? (Partnerschaft / Familie / Beruf / Gesundheit / Finanzen / Spirituelle Entwicklung)",
    ],
  },
  {
    id: "tier",
    title: "Für Tierkommunikation",
    optional: true,
    fields: [
      "Name des Tieres",
      "Tierart",
      "Alter",
      "Seit wann lebt das Tier bei dir?",
      "Welche Fragen möchtest du deinem Tier stellen?",
      "Gibt es aktuelle Verhaltensauffälligkeiten oder Themen?",
    ],
    note: "Bitte sende zusätzlich ein aktuelles Foto deines Tieres.",
  },
  {
    id: "abschluss",
    title: "Abschluss",
    fields: [
      "Gibt es noch etwas, das ich vor unserer Sitzung wissen sollte?",
    ],
    disclaimer:
      "Mit dem Absenden dieses Fragebogens bestätige ich, dass die angebotene Leistung eine energetische und spirituelle Begleitung darstellt und keinen Ersatz für medizinische, psychologische oder tierärztliche Diagnosen oder Behandlungen bietet.",
  },
];

export default function Fragebogen() {
  return (
    <div className="bg-[#050510] text-[#F0EDE8] min-h-screen pt-32 pb-24">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <p className="text-[0.65rem] uppercase tracking-[0.4em] text-[#C9A84C] mb-4">
            Auralia Healing
          </p>
          <h1
            className="text-4xl md:text-5xl font-light text-[#F0EDE8] mb-4"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Vorbereitungsfragebogen
          </h1>
          <p className="text-[0.65rem] uppercase tracking-[0.25em] text-[#9A9A9A]">
            Für energetische Begleitung
          </p>
        </motion.div>

        {/* Intro */}
        <motion.div
          className="bg-[#0D0D1A] border border-[#C9A84C]/20 rounded-2xl p-8 mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          <h2
            className="text-xl font-light text-[#F0EDE8] mb-3"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Herzlich willkommen.
          </h2>
          <p className="text-[#9A9A9A] text-sm leading-relaxed">
            Vielen Dank für dein Vertrauen. Bitte nimm dir etwas Zeit, die
            folgenden Fragen möglichst ehrlich und ausführlich zu beantworten.
            Deine Antworten helfen mir dabei, mich bestmöglich auf unsere
            gemeinsame Arbeit einzustimmen.
          </p>
        </motion.div>

        {/* Print button */}
        <motion.div
          className="flex justify-end gap-3 mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 border border-[#1A1A2E] text-[#9A9A9A] text-xs uppercase tracking-[0.15em] px-5 py-2.5 hover:border-[#C9A84C]/40 hover:text-[#F0EDE8] transition-all duration-300"
            data-testid="fragebogen-print-btn"
          >
            <Printer size={14} />
            Drucken / Als PDF speichern
          </button>
        </motion.div>

        {/* Sections */}
        <div className="space-y-8">
          {sections.map((section, si) => (
            <motion.div
              key={section.id}
              className="bg-[#0D0D1A] border border-[#1A1A2E] rounded-2xl p-8"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: si * 0.07 }}
              data-testid={`fragebogen-section-${section.id}`}
            >
              <div className="flex items-center gap-3 mb-6">
                <div
                  className="text-2xl text-[#C9A84C]/30 font-light w-8"
                  style={{ fontFamily: "Cormorant Garamond, serif" }}
                >
                  {String(si + 1).padStart(2, "0")}
                </div>
                <h2
                  className="text-xl font-light text-[#F0EDE8]"
                  style={{ fontFamily: "Cormorant Garamond, serif" }}
                >
                  {section.title}
                  {section.optional && (
                    <span className="text-[#9A9A9A] text-sm ml-2">(nur für Tierkommunikation)</span>
                  )}
                </h2>
              </div>

              <div className="space-y-5">
                {section.fields.map((field, fi) => (
                  <div key={fi} className="border-b border-[#1A1A2E] pb-5">
                    <p className="text-sm text-[#9A9A9A] mb-3">{field}</p>
                    <div className="h-8 border-b border-dashed border-[#1A1A2E]/60" />
                  </div>
                ))}
              </div>

              {section.note && (
                <p className="mt-5 text-xs text-[#C9A84C] italic">{section.note}</p>
              )}

              {section.disclaimer && (
                <div className="mt-6 p-4 border border-[#1A1A2E] rounded-xl">
                  <p className="text-[#9A9A9A] text-xs leading-relaxed">
                    <strong className="text-[#F0EDE8]">Hinweis: </strong>
                    {section.disclaimer}
                  </p>
                  <div className="mt-4 flex items-center gap-4">
                    <div>
                      <p className="text-[0.6rem] uppercase tracking-[0.15em] text-[#9A9A9A] mb-1">Datum</p>
                      <div className="w-32 border-b border-dashed border-[#1A1A2E]/60 h-6" />
                    </div>
                    <div>
                      <p className="text-[0.6rem] uppercase tracking-[0.15em] text-[#9A9A9A] mb-1">Unterschrift</p>
                      <div className="w-48 border-b border-dashed border-[#1A1A2E]/60 h-6" />
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Footer note */}
        <motion.div
          className="mt-10 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <p className="text-[#9A9A9A] text-sm mb-2">
            Bitte sende diesen Fragebogen ausgefüllt an:
          </p>
          <a
            href="mailto:auralia-healing@gmx.de"
            className="text-[#C9A84C] hover:underline underline-offset-2"
          >
            auralia-healing@gmx.de
          </a>
          <p className="text-[#9A9A9A] text-xs mt-4">
            oder per WhatsApp:{" "}
            <a
              href="https://wa.me/491556724186"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#C9A84C]"
            >
              +49 155 6724186
            </a>
          </p>
          <div className="mt-8">
            <Link
              to="/leistungen"
              className="text-[#9A9A9A] text-xs uppercase tracking-[0.2em] hover:text-[#C9A84C] transition-colors"
            >
              Zurück zu den Leistungen
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
